import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import type { TeamMember } from "../types";

interface AuthContextValue {
  session: Session | null;
  profile: TeamMember | null;
  loading: boolean;
  authError: string;
  passwordRecovery: boolean;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  updatePassword(password: string): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchProfile(userId: string): Promise<TeamMember | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from("team_members")
    .select("*")
    .eq("user_id", userId)
    .eq("active", true)
    .maybeSingle();
  if (error) throw error;
  return data as TeamMember | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [authError, setAuthError] = useState("");
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    const authClient = supabase;
    let mounted = true;

    const hydrate = async (nextSession: Session | null) => {
      if (!mounted) return;
      setSession(nextSession);
      if (!nextSession) {
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        const nextProfile = await fetchProfile(nextSession.user.id);
        if (!nextProfile) {
          setAuthError("This account is not enabled for the House Care team.");
          await authClient.auth.signOut();
          return;
        }
        if (mounted) {
          setProfile(nextProfile);
          setAuthError("");
        }
      } catch (error) {
        if (mounted) {
          setAuthError(
            error instanceof Error ? error.message : "Could not verify access.",
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void authClient.auth.getSession().then(({ data }) => hydrate(data.session));
    const { data: listener } = authClient.auth.onAuthStateChange(
      (event, nextSession) => {
        if (event === "PASSWORD_RECOVERY") setPasswordRecovery(true);
        void hydrate(nextSession);
      },
    );

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      profile,
      loading,
      authError,
      passwordRecovery,
      async signIn(email, password) {
        if (!supabase) throw new Error("Supabase is not configured.");
        setAuthError("");
        const { data, error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) throw error;
        if (!data.user) throw new Error("Login did not return a user.");
        const nextProfile = await fetchProfile(data.user.id);
        if (!nextProfile) {
          await supabase.auth.signOut();
          throw new Error("This account is not enabled for the House Care team.");
        }
        setProfile(nextProfile);
      },
      async signOut() {
        if (!supabase) return;
        await supabase.auth.signOut();
        setPasswordRecovery(false);
      },
      async requestPasswordReset(email) {
        if (!supabase) throw new Error("Supabase is not configured.");
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
      },
      async updatePassword(password) {
        if (!supabase) throw new Error("Supabase is not configured.");
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        setPasswordRecovery(false);
      },
    }),
    [authError, loading, passwordRecovery, profile, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
